import type {
  Actor,
  Command,
  JobView,
  GuildRecord,
} from "@guildmaster/core/platform";
import type { Report, ReportSummary } from "@guildmaster/reports";
import { canonicalJson } from "@guildmaster/core/platform";
import type { MemberSummary } from "@guildmaster/core/member-portal";
let csrf = "";
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}
export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers: {
      ...(options.body
        ? { "Content-Type": "application/json", "X-CSRF-Token": csrf }
        : {}),
      ...options.headers,
    },
  });
  if (response.status === 204) return undefined as T;
  const value: unknown = await response.json();
  if (!response.ok)
    throw new ApiError(
      response.status,
      typeof value === "object" && value && "error" in value
        ? String(value.error)
        : "Request failed",
    );
  return value as T;
}
export interface PortalConfig {
  guildName: string;
  stage: string;
  signInConfigured: boolean;
  jobsEnabled: boolean;
  recordsEnabled: boolean;
  deliveryEnabled: boolean;
  onboardingEnabled: boolean;
  liveLootEnabled: boolean;
  plannerEnabled: boolean;
  professionsEnabled: boolean;
}
export interface RunnerStatus {
  online: boolean;
  lastSeenAt: number | null;
  paused: boolean;
  backup: {
    created_at: number;
    file_count: number;
    source_bytes: number;
    complete: number;
  } | null;
  incident: { state: string; checkedAt: number } | null;
  details: {
    buildRevision?: string;
    backupAt: number | null;
    codex: string;
    accepting: boolean;
    usage: {
      inputTokens: number;
      outputTokens: number;
      unknownTasks: number;
      localWeeklyBudget: number;
    };
  } | null;
}
export const client = {
  async dashboard() {
    const value = await api<{
      actor: Actor;
      csrf: string;
      reports: ReportSummary[];
      records: GuildRecord[];
      jobs: JobView[];
      status: RunnerStatus;
    }>("/api/dashboard");
    csrf = value.csrf;
    return value;
  },
  memberSummary: (context: string, characterId?: string) => api<MemberSummary>("/api/member-summary?" + new URLSearchParams({ context, ...(characterId ? { characterId } : {}) })),
  config: () => api<PortalConfig>("/api/config"),
  async me() {
    const current = await api<{ actor: Actor; csrf: string }>("/api/me");
    csrf = current.csrf;
    return current.actor;
  },
  reports: () => api<ReportSummary[]>("/api/reports"),
  report: (id: string, revision?: number) =>
    api<Report>(
      "/api/reports/" +
        encodeURIComponent(id) +
        (revision ? "/" + revision : ""),
    ),
  revisions: (id: string) =>
    api<{ revision: number; publishedAt: number }[]>(
      "/api/reports/" + encodeURIComponent(id) + "/revisions",
    ),
  records: () => api<GuildRecord[]>("/api/records"),
  jobs: () => api<JobView[]>("/api/jobs"),
  status: () => api<RunnerStatus>("/api/status"),
  async approveReport(report: Report, rationale: string) {
    const bytes = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(canonicalJson(report)),
    );
    const expectedHash = Array.from(new Uint8Array(bytes), (value) =>
      value.toString(16).padStart(2, "0"),
    ).join("");
    return api(
      "/api/report-drafts/" + report.id + "/" + report.revision + "/approve",
      {
        method: "POST",
        body: JSON.stringify({
          expectedHash,
          rationale,
          confirmation: "publish-reviewed-report",
        }),
      },
    );
  },
  withdrawReport: (id: string, reason: string) =>
    api("/api/reports/" + id + "/withdraw", {
      method: "POST",
      body: JSON.stringify({ reason, confirmation: "withdraw-all-revisions" }),
    }),
  submit: (command: Command, key: string) =>
    api<JobView>("/api/jobs", {
      method: "POST",
      body: JSON.stringify(command),
      headers: { "Idempotency-Key": key },
    }),
  changeJob: (id: string, action: "cancel" | "resume") =>
    api("/api/jobs/" + id, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  pause: (paused: boolean) =>
    api("/api/operations", {
      method: "POST",
      body: JSON.stringify({ paused }),
    }),
  logout: () => api("/auth/logout", { method: "POST", body: "{}" }),
};
